import React, { useState, useEffect } from 'react';
import { getAvailableApis, getEnabledApis, requestApiActivation, getApiActivationRequests } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

// Extract array from response where res.data is a raw (possibly truncated) JSON string
const extractArray = (res) => {
  // Handle different response structures
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  if (res?.data && Array.isArray(res.data)) return res.data;
  
  // Handle case where res.data is an array directly
  if (Array.isArray(res?.data)) return res.data;
  
  // Handle case where res.data is undefined/null
  if (!res?.data) return [];
  
  // Fallback: return empty array
  return [];
};

// Derive a human-readable group name from apiName
const getGroupKey = (api) => {
  // Use the first word of the apiName as the group key
  const name = api.apiName || '';
  const prefix = name.split(' ')[0];
  return prefix || 'OTHER';
};

// Map group keys to friendly display names
const GROUP_LABELS = {
  OTP:     'OTP Services',
  BBPS:    'BBPS Services',
  DMT:     'DMT Services',
  AEPS:    'AEPS Services',
  UPI:     'UPI Services',
  RECHARGE:'Recharge Services',
  PAN:     'PAN Services',
  KYC:     'KYC Services',
  NEFT:    'NEFT Services',
  IMPS:    'IMPS Services',
  RTGS:    'RTGS Services',
  BANK:    'Banking Services',
  WALLET:  'Wallet Services',
  PAYMENT: 'Payment Services',
  REPORT:  'Report Services',
  OTHER:   'Other Services',
};

const getGroupLabel = (key) => GROUP_LABELS[key] || `${key} Services`;

const Apis = () => {
  const [available, setAvailable] = useState([]);
  const [enabled, setEnabled]     = useState([]);
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // { group, apis[] }
  const [form, setForm]           = useState({ portalUrl: '', useCase: '' });
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded]   = useState({}); // track which groups are expanded

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [avRes, enRes, reqRes] = await Promise.all([
        getAvailableApis(),
        getEnabledApis(),
        getApiActivationRequests(),
      ]);
      
      setAvailable(extractArray(avRes));
      setEnabled(extractArray(enRes));
      setRequests(extractArray(reqRes));
    } catch (err) {
      toast.error('Failed to load APIs');
      setAvailable([]);
      setEnabled([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Group available APIs by prefix
  const grouped = available.reduce((acc, api) => {
    const key = getGroupKey(api);
    if (!acc[key]) acc[key] = [];
    acc[key].push(api);
    return acc;
  }, {});

  const isApiEnabled  = (apiId) => enabled.some(e => e.apiId === apiId);
  const isApiPending  = (apiId) => requests.some(r => r.apiId === apiId && r.status === 'PENDING');

  // Group-level status: all enabled, some enabled, pending, or none
  const getGroupStatus = (apis) => {
    const allEnabled  = apis.every(a => isApiEnabled(a.apiId));
    const someEnabled = apis.some(a => isApiEnabled(a.apiId));
    const anyPending  = apis.some(a => isApiPending(a.apiId));
    if (allEnabled)  return 'all_enabled';
    if (someEnabled) return 'partial';
    if (anyPending)  return 'pending';
    return 'none';
  };

  const handleRequestGroup = (groupKey, apis) => {
    setSelected({ group: groupKey, apis });
    setForm({ portalUrl: '', useCase: '' });
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      // Request access for all APIs in the group that aren't already enabled/pending
      const toRequest = selected.apis.filter(
        a => !isApiEnabled(a.apiId) && !isApiPending(a.apiId)
      );
      await Promise.all(
        toRequest.map(api =>
          requestApiActivation({ 
            apiId: String(api.apiId), // Use api.apiId instead of api.id
            portalUrl: form.portalUrl,
            useCase: form.useCase
          })
        )
      );
      showSuccessToast(`Access requested for ${toRequest.length} API${toRequest.length !== 1 ? 's' : ''} in ${getGroupLabel(selected.group)}`);
      setSelected(null);
      setForm({ portalUrl: '', useCase: '' });
      loadData();
    } catch (err) {
      console.error('API activation request failed:', err);
      const errorMessage = err.response?.data?.message || 
                           err.response?.data?.error || 
                           err.message || 
                           'Failed to submit request';
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        <span className="text-gray-600">Loading APIs...</span>
      </div>
    );
  }

  const groupKeys = Object.keys(grouped).sort();

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">APIs</h1>
      <p className="text-sm text-gray-500 mb-6">
        {groupKeys.length} service{groupKeys.length !== 1 ? 's' : ''} available · {available.length} total endpoints
      </p>

      {groupKeys.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg">No APIs available.</p>
          <p className="text-sm mt-1">Please contact support if this persists.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {groupKeys.map(key => {
            const apis    = grouped[key];
            const status  = getGroupStatus(apis);
            const isOpen  = !!expanded[key];
            const enabledCount = apis.filter(a => isApiEnabled(a.apiId)).length;

            return (
              <Card key={key} className="overflow-hidden">
                <CardContent className="p-0">
                  {/* Group Header Row */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Expand/Collapse toggle */}
                      <button
                        onClick={() => toggleExpand(key)}
                        className="text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                      >
                        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{getGroupLabel(key)}</span>
                          {status === 'all_enabled' && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {key}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {apis.length} endpoint{apis.length !== 1 ? 's' : ''}
                          {enabledCount > 0 && ` · ${enabledCount} enabled`}
                        </p>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex-shrink-0 ml-4">
                      {status === 'all_enabled' ? (
                        <Badge className="bg-green-100 text-green-800 border-0">All Enabled</Badge>
                      ) : status === 'partial' ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-800 border-0">
                            {enabledCount}/{apis.length} Enabled
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRequestGroup(key, apis)}
                          >
                            Request Remaining
                          </Button>
                        </div>
                      ) : status === 'pending' ? (
                        <Badge className="bg-amber-100 text-amber-800 border-0">Pending Approval</Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleRequestGroup(key, apis)}
                        >
                          Request Access
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expandable endpoint list */}
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-gray-50">
                      {apis.map((api, idx) => (
                        <div
                          key={api.apiId}
                          className={`flex items-center justify-between px-6 py-2.5 text-sm ${
                            idx < apis.length - 1 ? 'border-b border-gray-100' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isApiEnabled(api.apiId)
                              ? <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                              : <div className="h-3.5 w-3.5 rounded-full border border-gray-300 flex-shrink-0" />
                            }
                            <span className="text-gray-700 truncate">{api.apiName}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <span className="text-xs text-gray-400">
                              {api.chargeType === 'FIXED'
                                ? `₹${api.fixedCharge ?? api.defaultFixedCharge ?? 0}`
                                : `${api.percentageCharge ?? api.defaultPercentageCharge ?? 0}%`
                              } + {api.gstPercentage ?? api.defaultGstPercentage ?? 0}% GST
                            </span>
                            {isApiEnabled(api.apiId) ? (
                              <Badge className="bg-green-100 text-green-700 text-xs border-0 py-0">Enabled</Badge>
                            ) : isApiPending(api.apiId) ? (
                              <Badge className="bg-amber-100 text-amber-700 text-xs border-0 py-0">Pending</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-500 text-xs border-0 py-0">Not Enabled</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Request Access Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Request Access — {selected ? getGroupLabel(selected.group) : ''}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-1">
              {/* Show which endpoints will be requested */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-gray-500 font-medium mb-2">Requesting access to:</p>
                <ul className="space-y-1">
                  {selected.apis
                    .filter(a => !isApiEnabled(a.apiId) && !isApiPending(a.apiId))
                    .map(a => (
                      <li key={a.apiId} className="flex items-center gap-2 text-gray-700">
                        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                        {a.apiName}
                      </li>
                    ))
                  }
                </ul>
              </div>

              <div>
                <Label>Portal URL</Label>
                <Input
                  value={form.portalUrl}
                  onChange={(e) => setForm({ ...form, portalUrl: e.target.value })}
                  placeholder="https://your-portal.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Use Case</Label>
                <Textarea
                  value={form.useCase}
                  onChange={(e) => setForm({ ...form, useCase: e.target.value })}
                  placeholder="Describe how you'll use these APIs..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={submitting || !form.portalUrl || !form.useCase}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Apis;